import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors, fonts, sizes, spacing, radius } from '../styles/designTokens';
import BaseButton from '../components/common/BaseButton';
import { useLocation } from 'react-router-dom';
import { IconCheck, IconSparkles } from '@tabler/icons-react';

// ===== TRANSLATIONS =====
const welcomeTranslations = {
    en: {
        welcome: "Welcome to KrushiSetu!",
        accCreated1: ", your account has been created successfully",
        accCreated2: "as a ",
        role: "Buyer",
        accCreated3: "",
        infoText: "As a buyer, you can buy crops, seeds, fertilizers directly from farmers!",
        goHome: "Go to Home"
    },
    hi: {
        welcome: "कृषिसेतु में आपका स्वागत है!",
        accCreated1: ", आपका खाता सफलतापूर्वक",
        accCreated2: "एक ",
        role: "ख़रीददार",
        accCreated3: " के रूप में बनाया गया है",
        infoText: "एक ख़रीददार के रूप में, आप सीधे किसानों से फसल, बीज और उर्वरक खरीद सकते हैं!",
        goHome: "होम पर जाएं"
    },
    mr: {
        welcome: "कृषीसेतू मध्ये आपले स्वागत आहे!",
        accCreated1: ", तुमचे खाते यशस्वीरित्या तयार झाले आहे",
        accCreated2: "",
        role: "खरेदीदार",
        accCreated3: " म्हणून",
        infoText: "खरेदीदार म्हणून, तुम्ही थेट शेतकऱ्यांकडून पिके, बियाणे आणि खते खरेदी करू शकता!",
        goHome: "मुख्यपृष्ठावर जा"
    }
};

const WelcomeBuyerScreen = ({ userName: propUserName, onGoHome }) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    // Prioritize the name passed in route state, then props, then default
    const userName = location.state?.userName || propUserName || "{Buyer name}";

    // Fallback to 'en' if the current language is not in our specific translation object
    const langCode = i18n.language || 'en';
    const currentLang = langCode.startsWith('hi') ? 'hi' : (langCode.startsWith('mr') || langCode.startsWith('mar')) ? 'mr' : 'en';
    const localT = welcomeTranslations[currentLang];

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: colors.background_light,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: '1rem', sm: 'clamp(1rem, 2vh, 1.5rem)' }, // padding rems/vh to scale vertically
            fontFamily: fonts.poppins,
            width: '100%' // layout width
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: '448px', // max-width px
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
            }}>

                {/* Main Success Checkmark Icon */}
                <Box sx={{
                    width: 'clamp(4rem, 10vh, 5.5rem)', // Size tuned to image proportion
                    height: 'clamp(4rem, 10vh, 5.5rem)',
                    borderRadius: '50%',
                    backgroundColor: "colors.primary_rgba_10", // Exact green background block
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 'clamp(1rem, 2vh, 1.5rem)'
                }}>
                    <IconCheck size={36} color={colors.primary} stroke={3} />
                </Box>

                {/* Headings */}
                <Typography sx={{
                    color: colors.text_dark,
                    fontSize: 'clamp(1.25rem, 4vw, 1.5rem)', // 20-24px exact match
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: '-0.046rem',
                    mb: 'clamp(0.25rem, 1.5vh, 0.75rem)',
                    fontFamily: fonts.poppins,
                    width: '100%',
                    maxWidth: '320px'
                }}>
                    {localT.welcome}
                </Typography>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    mb: 'clamp(1.5rem, 3vh, 2rem)' // Exact padding match to image
                }}>
                    <Typography sx={{
                        color: colors.text, // Gray subtext
                        fontSize: '1rem', // 16px exact size lock
                        fontWeight: 400, // Regular
                        lineHeight: 1.5, // 24px leading
                        letterSpacing: 0,
                        fontFamily: fonts.poppins,
                        whiteSpace: 'normal', // Allow natural soft wrap to match layout
                        textAlign: 'center',
                        width: '100%',
                        maxWidth: '434px', // Constrain to Figma width block match
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                    }}>
                        {userName}{localT.accCreated1}{" "}
                        <Box component="span" sx={{ display: 'inline-block' }}>
                            {localT.accCreated2}
                            <Box component="span" sx={{ fontWeight: 600, color: colors.text_dark }}>
                                {localT.role}
                            </Box>
                            {localT.accCreated3}
                        </Box>
                    </Typography>
                </Box>

                {/* Go to Home Button */}
                <BaseButton
                    onClick={onGoHome}
                    icon={<IconSparkles size={20} />}
                    sx={{
                        width: '100%',
                        height: 'clamp(3rem, 6vh, 3.5rem)',
                        borderRadius: '2rem',
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)', // Match button font size
                        fontWeight: 600,
                        boxShadow: colors.shadow_button_primary,
                        mt: 0 // Removes margin to match spacing
                    }}
                >
                    {localT.goHome}
                </BaseButton>

            </Box>
        </Box>
    );
};

export default WelcomeBuyerScreen;
